import React, { useEffect } from 'react';
import { BrowserRouter as Router } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { Toaster } from 'react-hot-toast';
import AppRoutes from './AppRoutes';
import { useAuthStore, cleanupAuthStore } from './stores/authStore';

// Create a query client for React Query
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      retry: 1,
      refetchOnWindowFocus: false,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  },
});

// Expose QueryClient for E2E testing (staging only)
// Multi-layer security: environment variable + hostname validation
// This enables Playwright tests to inspect React Query cache for VULN-003 validation
if (
  import.meta.env.VITE_APP_ENV === 'staging' ||
  window.location.hostname.includes('staging.rsvd.online')
) {
  (window as any).__REACT_QUERY_CLIENT__ = queryClient;
  console.log('[E2E Testing] QueryClient exposed for cache inspection');
}

// Auth initializer component
const AuthInitializer: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const initializeAuth = useAuthStore((state) => state.initializeAuth);

  useEffect(() => {
    initializeAuth();

    // Cleanup auth subscription on unmount to prevent memory leaks
    return () => {
      cleanupAuthStore();
    };
  }, [initializeAuth]);

  return <>{children}</>;
};

function App() {
  return (
    <QueryClientProvider client={queryClient}>
      <Router>
        <AuthInitializer>
          <div className="min-h-screen bg-gray-50">
            <AppRoutes />
            <Toaster
              position="top-right"
              toastOptions={{
                duration: 4000,
                style: {
                  background: '#363636',
                  color: '#fff',
                },
                success: {
                  duration: 3000,
                  style: {
                    background: '#10b981',
                  },
                },
                error: {
                  duration: 5000,
                  style: {
                    background: '#ef4444',
                  },
                },
              }}
            />
          </div>
        </AuthInitializer>
      </Router>
    </QueryClientProvider>
  );
}

export default App;